import { useState } from 'react';
import * as _ from 'lodash';
import type { VoidFunctionComponent } from 'react';
import useAPITokens from './useAPITokens';
import Loader from '../../../generic/Loader';
import { ErrorAlert, SearchInput } from '../../../generic';
import type { APITokenResponse } from '../../../../../shared/settings/apiTokens';
import APITokenTable from './APITokenTable';
import Button, { BUTTON_SIZE } from '../../../generic/Button';
import type { ModalMode } from './ModalCreateOrEditToken';
import ModalCreateOrEditToken from './ModalCreateOrEditToken';
import styles from './apiTokens.module.less';

type TokenBodyProps = {
  tokens: APITokenResponse[];
  onDeleteToken: (id: number) => void;
  onEditToken: (tokenId: number) => void;
};

const APITokenBody: VoidFunctionComponent<TokenBodyProps> = ({
  tokens,
  onDeleteToken,
  onEditToken,
}) => {
  if (_.isEmpty(tokens)) {
    return (
      <div className="service-connected-container">
        <h4 className="text-center">No API tokens found.</h4>
      </div>
    );
  }

  return (
    <APITokenTable
      tokens={tokens}
      onDeleteToken={onDeleteToken}
      onEditToken={onEditToken}
    />
  );
};

const APITokenSettings: VoidFunctionComponent = () => {
  const {
    apiTokens: allTokens,
    loadingState: {
      isReading: isLoading,
      isMutating,
    },
    errorState: {
      readErrors,
      mutateErrors,
    },
    onCreateToken,
    onUpdateToken,
    onDeleteToken,
  } = useAPITokens();

  const [modalMode, setModalMode] = useState<ModalMode>({
    mode: 'Hide',
    token: null,
  });

  const [searchValue, setSearchValue] = useState<string>('');

  // Should this logic live in the hook?
  // Filter tokens by search term if one is entered
  const apiTokens = allTokens.filter(token => {
    if (!searchValue) {
      return true;
    }

    return (
      (token.name && token.name.includes(searchValue)) ||
      (token.username && token.username.includes(searchValue)) ||
      (token.email && token.email.includes(searchValue))
    );
  });

  const onSetTokenToEdit = (id: number) => {
    const nextToken = apiTokens.find(t => t.id === id);
    if (nextToken) {
      setModalMode({
        token: nextToken,
        mode: 'Edit',
      });
    }
  };

  const onHideModal = () => {
    setModalMode({
      mode: 'Hide',
      token: null,
    });
  };

  const onAddToken = () => {
    setModalMode({
      mode: 'Create',
      token: null,
    });
  };

  return (
    <>
      <div className="text-center margin-bottom-sm">
        <h1>API Tokens</h1>
        <p>
          Tokens you have generated that can be used to access the FOSSA API.
          Apps using these tokens impersonate your identity.
        </p>
        <p>
          Push-Only tokens can only be used to send data to FOSSA. This allows
          your CI to safely expose them to 3rd parties.
        </p>
      </div>
      <div className={styles.tableHeader}>
        <div className={styles.searchBar}>
          <SearchInput
            value={searchValue}
            placeholder="Search by token name or User"
            label="search-by-token"
            onChange={ev => setSearchValue(ev)}
          />
        </div>
        <div className="margin-bottom-sm">
          <Button
            size={BUTTON_SIZE.MEDIUM}
            onClick={onAddToken}
          >
            Add new token
          </Button>
        </div>
      </div>
      <div>
        {!_.isEmpty(readErrors) && <ErrorAlert status={{ errors: readErrors }} />}
        {!_.isEmpty(mutateErrors) && <ErrorAlert status={{ errors: mutateErrors }} />}
        {isLoading
          ? <Loader />
          : <APITokenBody
              onDeleteToken={onDeleteToken}
              onEditToken={onSetTokenToEdit}
              tokens={apiTokens}
            />}
      </div>
      <ModalCreateOrEditToken
        mode={modalMode}
        isLoading={isMutating}
        errors={mutateErrors}
        onHide={onHideModal}
        onUpdateToken={onUpdateToken}
        onCreateToken={onCreateToken}
      />
    </>
  );
};

export default APITokenSettings;
